/**
 * Response schema for GET /api/v4/artifacts/:artifactId/wiring
 * (src/api/v4/artifacts.ts), the admin debug view of how one artifact, its
 * blueprint and the in-code registries connect.
 *
 * The outer envelope and the execution rows are typed. The blueprint's nested
 * `actions`/`card` and the registry entries (`actionType`, `handler`,
 * `interaction`, `artifactCard`) are static in-code objects that the admin UI
 * validates deeply with its own WiringDataSchema; they stay `z.unknown()` here
 * so a registry field never turns a debug read into a 500.
 */
import { z } from 'zod';
import { DateOrIsoStringSchema } from './date-or-iso';
import { PrismaJsonColumnSchema } from './json-value';
import { ArtifactDataSchema } from './v4-artifact';

const WiringExecutionSchema = z.object({
  actionId: z.string(),
  outcome: PrismaJsonColumnSchema,
  deliveryStatus: z.string(),
  handlerSummary: z.string().nullable(),
  deliveryError: PrismaJsonColumnSchema,
  executionId: z.string(),
  id: z.string(),
  createdAt: DateOrIsoStringSchema,
  updatedAt: DateOrIsoStringSchema,
});

export const WiringResponseSchema = z.object({
  artifact: z.object({
    id: z.string(),
    kind: z.string(),
    data: ArtifactDataSchema,
    executions: z.array(WiringExecutionSchema),
    read: z.boolean(),
    createdAt: DateOrIsoStringSchema,
    updatedAt: DateOrIsoStringSchema,
  }),
  blueprint: z.object({
    kind: z.string(),
    displayName: z.string(),
    intent: z.string(),
    status: z.string(),
    artifactCard: z.string(),
    defaultView: z.string(),
    bundled: z.boolean(),
    card: z.record(z.string(), z.unknown()),
    detail: z.unknown(),
    actions: z.array(z.unknown()),
  }),
  wiring: z.object({
    artifactCard: z.unknown(),
    actions: z.array(
      z.object({
        action: z.unknown(),
        actionType: z.unknown(),
        handler: z.unknown(),
        interaction: z.unknown(),
      }),
    ),
    cardSlots: z.array(
      z.object({
        slot: z.string(),
        dataKey: z.string(),
        resolvedValue: z.unknown(),
        status: z.enum(['hit', 'miss']),
      }),
    ),
    detailSections: z.array(
      z.object({
        key: z.string(),
        label: z.string(),
        resolvedValue: z.unknown(),
      }),
    ),
  }),
});
